import React, { FC, useId, useMemo, useState } from "react";
import { FiCalendar } from "react-icons/fi";

import { AdventDoor, AdventDoorInvite } from "apps/user/learner/api/types";
import useSendAdventDoorInvite from "apps/user/learner/hooks/use-send-advent-door-invite";
import {
  DoorInvite as ParsedInvite,
  formatInviteStart,
  formatInviteTime,
  inviteDuration,
  parseDoorInvite,
} from "apps/user/learner/lib/advent-door-invite";
import { useTranslation } from "lib/i18n/use-translation";
import { logError } from "utility/Function";

import { CompletionError } from "./mechanics/shared";
import styles from "../december-doors.module.scss";

interface DoorInviteProps {
  door: AdventDoor;
  invite: AdventDoorInvite;
}

const readInvite = (
  door: AdventDoor,
  invite: AdventDoorInvite
): ParsedInvite | null => {
  try {
    return parseDoorInvite(door, invite);
  } catch (error) {
    // The invite is an extra on the door: a slot this build cannot read is
    // reported and left out rather than take the whole sheet down.
    logError("Malformed advent door invite", error);
    return null;
  }
};

/** The door's offer to put its slot in the learner's calendar. */
const DoorInvite: FC<DoorInviteProps> = ({ door, invite }) => {
  const { t, i18n } = useTranslation(["decemberDoors"]);
  const sendInvite = useSendAdventDoorInvite();
  const [status, setStatus] = useState<"idle" | "sending" | "failed">("idle");
  // Whether this visit sent the invite, so the line can say where it went.
  const [isFresh, setIsFresh] = useState(false);
  const whenId = useId();
  const slot = useMemo(() => readInvite(door, invite), [door, invite]);

  if (!slot) return null;

  const send = async () => {
    setStatus("sending");
    try {
      const { alreadySent } = await sendInvite(door.id);
      setIsFresh(!alreadySent);
    } catch (error) {
      logError("Failed to send advent door invite", error);
      setStatus("failed");
      return;
    }
    setStatus("idle");
  };

  const sentLine = (sent: NonNullable<ParsedInvite["sent"]>): string => {
    const when = formatInviteStart(sent, i18n.language);
    if (isFresh && sent.delivery === "COMPANY_CALENDAR") {
      return t("decemberDoors:invite.sentCalendar", { when });
    }
    if (isFresh && sent.delivery === "EMAIL") {
      return t("decemberDoors:invite.sentEmail", { when });
    }
    return t("decemberDoors:invite.already", { when });
  };

  const duration = inviteDuration(slot);

  return (
    <div className={styles.invite} data-testid="door-invite">
      <div aria-live="polite">
        {/* The cache holds the sent invite before the request settles; keep
            the button until it does so the line names the right channel. */}
        {slot.sent && status !== "sending" ? (
          <p className={styles["invite-sent"]} data-testid="door-invite-sent">
            <FiCalendar aria-hidden />
            <span>{sentLine(slot.sent)}</span>
          </p>
        ) : (
          <div className={styles["invite-row"]}>
            <button
              type="button"
              className={styles["invite-button"]}
              onClick={send}
              disabled={status === "sending"}
              aria-describedby={whenId}
            >
              <FiCalendar aria-hidden />
              {status === "sending"
                ? t("decemberDoors:invite.sending")
                : t("decemberDoors:invite.add")}
            </button>
            <span id={whenId} className={styles["invite-when"]}>
              {t("decemberDoors:invite.when", {
                time: formatInviteTime(slot, i18n.language),
                duration: t(duration.key, { count: duration.count }),
              })}
            </span>
          </div>
        )}
      </div>
      <CompletionError show={status === "failed"} />
    </div>
  );
};

export default DoorInvite;
